/* Media remains native. The gallery never advances automatically. */
if (!customElements.get('asif-spaces-gallery')) {
  customElements.define('asif-spaces-gallery', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      this.abort = new AbortController();
      const { signal } = this.abort;
      this.slides = [...this.querySelectorAll('.spaces-gallery__slide')];
      if (!this.slides.length) return;
      this.index = 0;
      this.visible = false;
      this.playButton = this.querySelector('[data-play]');
      this.soundButton = this.querySelector('[data-sound]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.userPaused = false;
      this.allowMotion = false;
      this.querySelectorAll('[data-tools]').forEach(el => { el.hidden = false; });
      this.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (!button || !this.contains(button)) return;
        if (button.hasAttribute('data-next')) this.select(this.index + 1);
        if (button.hasAttribute('data-previous')) this.select(this.index - 1);
        if (button.hasAttribute('data-slide')) this.select(Number(button.dataset.slide));
        if (button.hasAttribute('data-sound')) {
          const video = this.slides[this.index]?.querySelector('video');
          if (!video) return;
          video.muted = !video.muted;
          this.sync();
        }
        if (button.hasAttribute('data-play')) {
          const video = this.slides[this.index]?.querySelector('video');
          if (!video) return;
          this.userPaused = !video.paused;
          if (video.paused) this.allowMotion = true;
          if (video.paused) video.play().catch(() => this.sync());
          else video.pause();
        }
      }, { signal });
      this.addEventListener('keydown', event => {
        // Keep video/CTA keyboard behavior native; only persistent slide
        // controls may switch slides without hiding the focused control.
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key) || !event.target.closest('[data-slide], [data-next], [data-previous]')) return;
        event.preventDefault();
        const index = event.key === 'Home' ? 0 : event.key === 'End' ? this.slides.length - 1 : this.index + (event.key === 'ArrowRight' ? 1 : -1);
        this.select(index);
      }, { signal });
      const viewport = this.querySelector('.spaces-cinema__viewport');
      viewport.addEventListener('pointerdown', event => {
        if (!event.isPrimary || event.button !== 0) return;
        this.gesture = { id: event.pointerId, x: event.clientX, y: event.clientY };
        viewport.setPointerCapture(event.pointerId);
      }, { signal });
      viewport.addEventListener('pointerup', event => {
        const start = this.gesture;
        this.gesture = null;
        if (!start || start.id !== event.pointerId) return;
        const dx = event.clientX - start.x;
        const dy = event.clientY - start.y;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.5) this.select(this.index + (dx < 0 ? 1 : -1));
      }, { signal });
      viewport.addEventListener('pointercancel', () => { this.gesture = null; }, { signal });
      viewport.addEventListener('dragstart', event => event.preventDefault(), { signal });
      this.querySelectorAll('.spaces-gallery__slide video').forEach(video => {
        video.controls = false;
        video.muted = true;
        video.addEventListener('volumechange', () => this.sync(), { signal });
        video.addEventListener('play', () => this.sync(), { signal });
        video.addEventListener('pause', () => this.sync(), { signal });
        video.addEventListener('timeupdate', () => {
          this.style.setProperty('--video-progress', Number.isFinite(video.duration) && video.duration > 0 ? video.currentTime / video.duration : 0);
        }, { signal });
      });
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.updatePlayback();
      }, { threshold: 0.15 });
      this.observer.observe(viewport);
      document.addEventListener('visibilitychange', () => {
        this.updatePlayback();
      }, { signal });
      this.motion.addEventListener('change', () => {
        this.allowMotion = false;
        this.updatePlayback();
      }, { signal });
      this.addEventListener('shopify:block:select', event => {
        const index = this.slides.findIndex(slide => slide === event.target || slide.contains(event.target));
        if (index >= 0) this.select(index);
      }, { signal });
      this.select(0);
    }
    select(index) {
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, i) => { slide.hidden = i !== this.index; });
      this.querySelectorAll('[data-slide]').forEach((button, i) => button.setAttribute('aria-pressed', String(i === this.index)));
      this.querySelector('.spaces-gallery__caption').textContent = this.slides[this.index].getAttribute('aria-label');
      this.querySelector('[data-counter]').textContent = `${String(this.index + 1).padStart(2, '0')} / ${String(this.slides.length).padStart(2, '0')}`;
      this.querySelector('.spaces-cinema__timeline').hidden = !this.slides[this.index].querySelector('video');
      this.updatePlayback();
      this.sync();
    }
    updatePlayback() {
      this.slides.forEach((slide, i) => {
        const video = slide.querySelector('video');
        if (!video) return;
        if (i === this.index && this.visible && !document.hidden && !this.userPaused && (!this.motion.matches || this.allowMotion)) video.play().catch(() => this.sync());
        else video.pause();
      });
    }
    sync() {
      const video = this.slides[this.index]?.querySelector('video');
      this.playButton.hidden = !video;
      if (this.soundButton) {
        this.soundButton.hidden = !video;
        const label = video?.muted ? this.soundButton.dataset.unmuteLabel : this.soundButton.dataset.muteLabel;
        this.soundButton.querySelector('[data-sound-text]').textContent = label;
        this.soundButton.title = label;
        this.soundButton.querySelector('[data-sound-icon]').setAttribute('d', video?.muted ? 'm16 9 6 6m0-6-6 6' : 'M15 8a6 6 0 0 1 0 8m3-11a10 10 0 0 1 0 14');
      }
      const playing = video && !video.paused;
      this.playButton.querySelector('[data-play-text]').textContent = playing ? this.playButton.dataset.pauseLabel : this.playButton.dataset.playLabel;
      this.playButton.querySelector('[data-play-icon]').setAttribute('d', playing ? 'M8 5v14M16 5v14' : 'M8 5v14l11-7Z');
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      this.querySelectorAll('video').forEach(video => video.pause());
    }
  });
}
